import { ImgID } from "../../image_loader/img_id.js";
import { formatMinutesShort } from "../../common/reward_format.js";
import { TextStyleScheme } from "../common_ui_elements/text_style_scheme.js";
import { ColorScheme } from "../common_ui_elements/color_scheme.js";
import { RoundedRectangle } from "../common_ui_elements/rounded_rectangle.js";
import { UIImage } from "../common_ui_elements/ui_image.js";
import { UINineSliceElement } from "../common_ui_elements/ui_nine_slice_element.js";
import {
  MyTextAlign,
  MyTextVerticalAlign,
  UITextElement,
} from "../common_ui_elements/ui_text_element.js";
import { AbstractDialog } from "./abstract_dialog.js";
import { DialogId } from "./dialog_id.js";
import { DialogManager } from "./dialog_manager.js";
import { OfferService } from "../../offers/offer_service.js";
import { isPurchaseErrorReported } from "../../offers/purchase_failure.js";
import type { OfferDto, OfferRewardDto } from "../../api/dto/offers.dto.js";
import { IapPlugin } from "../../plugins/iap_plugin.js";
import { adoptEconomyFromPlayer, refreshPlayerFromServer } from "../../api/player_sync.js";
import { LocalizationManager } from "../../localization/localization_manager.js";
import { formatShortDuration } from "../../common/common_funcs.js";
import { RewardFrame } from "../common_ui_elements/reward_frame.js";
import { crashCapture } from "../../crash/crash_reporter.js";
import { RewardTooltip } from "../tooltip/reward_tooltip.js";

/** Keeps button press feedback, but lets DialogManager clear its transparent
 * canvas before repainting the dialog. */
class DialogSafePressImage extends UIImage
{
  public override forceRenderParent(): void
  {
    this.forceMakeShouldBeRendered();
  }
}

export class RemoveAdsDialog extends AbstractDialog
{
  public static readonly dialog_id = DialogId.REMOVE_ADS_DIALOG;
  private purchase_in_progress = false;
  private price_text?: UITextElement;
  private readonly bonus_rewards: OfferRewardDto[];
  private timer_text?: UITextElement;
  private timer_end_ms: number | null;
  private next_timer_update_at = 0;
  private timer_expiry_refreshing = false;
  private readonly tall_layout_offset_design: number;

  constructor( private readonly offer: OfferDto, private readonly placement_id: string )
  {
    const bonus_rewards = RemoveAdsDialog.getBonusRewards( offer );
    const bonus_rows = Math.min( 2, Math.ceil( bonus_rewards.length / 4 ) );
    const body_w = LayoutScaler.widthFromDesign( 826 );
    const body_h_design = [ 1080, 1260, 1500 ][bonus_rows];
    const body_h = LayoutScaler.heightFromDesign( body_h_design );
    const tall_layout_offset_design = Math.max(
      0,
      ( LayoutScaler.getVisibleDesignHeight() - 1920 ) / 2
    );
    const visual_body_h = body_h - LayoutScaler.heightFromDesign(
      tall_layout_offset_design * 0.43
    );
    const body_x = ( DialogManager.getWidth() - body_w ) / 2;
    const body_y = ( DialogManager.getHeight() - visual_body_h ) / 2
      + LayoutScaler.heightFromDesign( 90 );

    super( DialogManager.ctx, body_x, body_y, body_w, body_h, true );

    this.bonus_rewards = bonus_rewards;
    this.timer_end_ms = OfferService.getOfferDisplayEndMs( offer );
    this.tall_layout_offset_design = tall_layout_offset_design;

    this.createBackground();
    this.createTimer();
    this.createTexts();
    this.createArtwork();
    this.createBonusRewards();
    this.createPurchaseButton();
    this.createCloseButton();
    void this.refreshPrice();
  }

  private createBackground(): void
  {
    const visual_body_h = this.rect.getH() - LayoutScaler.heightFromDesign(
      this.tall_layout_offset_design * 0.43
    );
    const background = new RoundedRectangle(
      this.ctx,
      0,
      0,
      this.rect.getW(),
      visual_body_h,
      {
        tl: 0,
        tr: 0,
        br: LayoutScaler.cornerRadiusFromDesign( 72 ),
        bl: LayoutScaler.cornerRadiusFromDesign( 72 ),
      },
      {
        type: "linear-gradient",
        direction: "vertical",
        stops: [
          { offset: 0, color: "rgba(8, 64, 207, 0)" },
          { offset: 0.42, color: "rgba(8, 64, 208, 0.62)" },
          { offset: 1, color: "rgba(8, 64, 208, 1)" },
        ],
      }
    );

    this.addChild( background );
  }

  /** Keeps the top content aligned to the 1920px Figma layout on tall phones,
   * while allowing lower controls to remain near the dialog bottom. */
  private adaptiveDialogY( design_y: number, top_anchor_strength: number ): number
  {
    return LayoutScaler.yFromDesign(
      design_y - this.tall_layout_offset_design * top_anchor_strength
    );
  }

  private createTexts(): void
  {
    const title_text = LocalizationManager.t( "REMOVE.ADS" ).replace( /\s+/g, " " ).trim();
    const title_x = LayoutScaler.xFromDesign( 20 );
    const title_w = this.rect.getW() - LayoutScaler.widthFromDesign( 40 );
    const title_y_design = this.timer_end_ms === null ? -63 : -25;
    const title_y = this.adaptiveDialogY( title_y_design, 0.8 );
    const title_h = LayoutScaler.heightFromDesign( 140 );
    const title_font = LayoutScaler.textSizeFromDesign( 100 );
    const title_min_font = LayoutScaler.textSizeFromDesign( 52 );

    const title_back = new UITextElement(
      this.ctx,
      title_x,
      title_y + LayoutScaler.yFromDesign( 18 ),
      title_w,
      title_h,
      {
        text: title_text,
        font_size: title_font,
        font_weight: 900,
        text_style: TextStyleScheme.geologica,
        text_color: "#0866B7",
        outline_width: LayoutScaler.widthFromDesign( 14 ),
        outline_color: "#0866B7",
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        scale_to_width: true,
        min_font_size: title_min_font,
      }
    );

    const title_middle = new UITextElement(
      this.ctx,
      title_x,
      title_y + LayoutScaler.yFromDesign( 9 ),
      title_w,
      title_h,
      {
        text: title_text,
        font_size: title_font,
        font_weight: 900,
        text_style: TextStyleScheme.geologica,
        text_color: "#0C90FF",
        outline_width: LayoutScaler.widthFromDesign( 14 ),
        outline_color: "#0C90FF",
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        scale_to_width: true,
        min_font_size: title_min_font,
      }
    );

    const title = new UITextElement(
      this.ctx,
      title_x,
      title_y + LayoutScaler.yFromDesign( 5 ),
      title_w,
      title_h,
      {
        text: title_text,
        font_size: title_font,
        font_weight: 900,
        text_style: TextStyleScheme.geologica,
        text_color: "#FFFFFF",
        gradient_text_color: {
          direction: "vertical",
          stops: [
            { offset: 0, color: "#FFFFFF" },
            { offset: 0.42, color: "#FFFFFF" },
            { offset: 1, color: "#8DD2FF" },
          ],
        },
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        scale_to_width: true,
        min_font_size: title_min_font,
      }
    );

    const description = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 60 ),
      this.adaptiveDialogY(
        this.timer_end_ms === null ? 105 : 145,
        0.85
      ),
      this.rect.getW() - LayoutScaler.widthFromDesign( 120 ),
      LayoutScaler.heightFromDesign( 108 ),
      {
        localization_key: this.offer.texts?.description_key ?? "REMOVE.ADS.TEXT",
        font_size: LayoutScaler.textSizeFromDesign( 43 ),
        font_weight: 900,
        text_style: TextStyleScheme.geologica,
        text_color: "#FFFFFF",
        outline_width: LayoutScaler.widthFromDesign( 8 ),
        outline_color: "#0C90FF",
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
      }
    );

    this.addChild( title_back );
    this.addChild( title_middle );
    this.addChild( title );
    this.addChild( description );
  }

  private createArtwork(): void
  {
    const art_w = LayoutScaler.widthFromDesign( 610 );
    const art_h = LayoutScaler.heightFromDesign( 436 );
    const art_x = ( this.rect.getW() - art_w ) / 2;

    const artwork = new UIImage(
      this.ctx,
      art_x,
      this.adaptiveDialogY( 245, 0.53 ),
      art_w,
      art_h,
      ImgID.UI_LoadingCannonWithBarrel
    );

    const badge_w = LayoutScaler.widthFromDesign( 250 );
    const badge_h = LayoutScaler.heightFromDesign( 250 );
    const badge = new UIImage(
      this.ctx,
      ( this.rect.getW() - badge_w ) / 2,
      this.adaptiveDialogY( 430, 0.53 ),
      badge_w,
      badge_h,
      ImgID.UI_RemoveAdsIcon
    );

    this.addChild( artwork );
    this.addChild( badge );
  }

  private createTimer(): void
  {
    if ( this.timer_end_ms === null ) return;

    // Keep the native 288:78 aspect ratio so the clock stays circular.
    const timer_w = LayoutScaler.widthFromDesign( 350 );
    const timer_h = LayoutScaler.heightFromDesign( 95 );
    const timer = new UIImage(
      this.ctx,
      ( this.rect.getW() - timer_w ) / 2,
      this.adaptiveDialogY( -114, 0.8 ),
      timer_w,
      timer_h,
      ImgID.UI_NoAdsTimer
    );
    const timer_text = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 82 ),
      0,
      timer_w - LayoutScaler.widthFromDesign( 100 ),
      timer_h,
      {
        text: "...",
        font_size: LayoutScaler.textSizeFromDesign( 54 ),
        font_weight: 900,
        text_style: TextStyleScheme.geologica,
        text_color: "#FFFFFF",
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        center_on_cap_height: true,
        ...TextStyleScheme.timerShadow(),
        auto_wrap: false,
        scale_to_width: true,
        min_font_size: LayoutScaler.textSizeFromDesign( 38 ),
      }
    );
    timer.addChild( timer_text, true );
    this.addChild( timer );
    this.timer_text = timer_text;
    this.updateTimer( performance.now(), true );
  }

  /** Up to eight server-configured rewards, arranged in at most two rows. */
  private createBonusRewards(): void
  {
    if ( !this.bonus_rewards.length ) return;

    const reward_count = this.bonus_rewards.length;
    const row_sizes = reward_count <= 4
      ? [ reward_count ]
      : [ Math.floor( reward_count / 2 ), Math.ceil( reward_count / 2 ) ];

    this.addChild( new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 55 ),
      this.adaptiveDialogY( 660, 0.38 ),
      this.rect.getW() - LayoutScaler.widthFromDesign( 100 ),
      LayoutScaler.heightFromDesign( 60 ),
      {
        localization_key: "PLUS_BONUS",
        font_size: LayoutScaler.textSizeFromDesign( 48 ),
        font_weight: 900,
        text_style: TextStyleScheme.geologica,
        text_color: '#FFFFFF',
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.widthFromDesign( 14 ),
        outline_color: '#0A85EE',
        scale_to_width: true,
        auto_wrap: false,
      }
    ) );

    let reward_index = 0;
    for ( let row = 0; row < row_sizes.length; row++ )
    {
      const row_size = row_sizes[row];
      const row_rewards = this.bonus_rewards.slice( reward_index, reward_index + row_size );
      reward_index += row_size;

      const card_w = LayoutScaler.widthFromDesign( 163 );
      const card_h = LayoutScaler.heightFromDesign( 202 );
      const icon_w = LayoutScaler.widthFromDesign( 150 );
      const icon_h = LayoutScaler.heightFromDesign( 150 );
      const card_gap_design = 25;
      const gap = LayoutScaler.widthFromDesign( card_gap_design );
      const row_w = row_rewards.length * card_w + ( row_rewards.length - 1 ) * gap;
      const start_x = ( this.rect.getW() - row_w ) / 2;
      const card_y = this.adaptiveDialogY( 800 + row * ( 202 + card_gap_design ), 0.5 );
      const layouts = row_rewards.map( ( reward, index ) =>
      {
        const x = start_x + index * ( card_w + gap );
        const image_w = reward.type === 'coins' ? LayoutScaler.widthFromDesign( 155 ) : icon_w;
        const image_h = reward.type === 'coins' ? LayoutScaler.heightFromDesign( 118 ) : icon_h;
        return {
          reward,
          x,
          image_w,
          image_h,
          image_x: x + ( card_w - image_w ) / 2,
        };
      } );
      const visible_left = Math.min( ...layouts.map( layout =>
      {
        const bounds = this.rewardVisibleHorizontalBounds( layout.reward.type );
        return layout.image_x + layout.image_w * bounds.left;
      } ) );
      const visible_right = Math.max( ...layouts.map( layout =>
      {
        const bounds = this.rewardVisibleHorizontalBounds( layout.reward.type );
        return layout.image_x + layout.image_w * bounds.right;
      } ) );
      const visual_offset_x = this.rect.getW() / 2 - ( visible_left + visible_right ) / 2;

      layouts.forEach( layout =>
      {
        const card = new RewardFrame(
          this.ctx,
          layout.x + visual_offset_x,
          card_y,
          {
            icon_id: this.rewardIcon( layout.reward.type ),
            amount_text: this.rewardAmount( layout.reward ),
            variant: "compact",
            icon_width: layout.image_w,
            icon_height: layout.image_h,
          }
        );
        RewardTooltip.attach( card, layout.reward.type );
        this.addChild( card );
      } );
    }
  }

  private static getBonusRewards( offer: OfferDto ): OfferRewardDto[]
  {
    const raw = offer.rewards ?? offer.data?.rewards;
    const rewards = Array.isArray( raw )
      ? raw.filter( ( value ): value is OfferRewardDto =>
        Boolean( value ) && typeof value === 'object' && typeof ( value as OfferRewardDto ).type === 'string'
      )
      : raw && typeof raw === 'object'
        ? Object.entries( raw ).flatMap( ( [ type, amount ] ) =>
          typeof amount === 'number' && amount > 0
            ? [{ type: type as OfferRewardDto['type'], amount }]
            : []
        )
        : [];

    return rewards
      .filter( reward => reward.type !== 'no_ads' && reward.amount > 0 )
      .slice( 0, 8 );
  }

  private rewardIcon( type: OfferRewardDto['type'] ): ImgID
  {
    switch ( type )
    {
      case 'coins': return ImgID.UI_ShopBlockImg;
      case 'hearts': return ImgID.IconHeart;
      case 'infinite_hearts_minutes': return ImgID.UI_ShopInfiniteHeart;
      case 'big_balls': return ImgID.UI_GameStartBigBallBooster;
      case 'infinite_balls': return ImgID.UI_GameStartInfiniteBallsBooster;
      case 'multi_balls': return ImgID.UI_GameStartMultiballsBooster;
      case 'fireworks': return ImgID.UI_GameStartFireworkBooster;
      case 'mines': return ImgID.UI_PassiveBoosterBomb;
      case 'level_shots': return ImgID.UI_DailyTasksTaskCannon;
      case 'no_ads': case 'no_ads_minutes': return ImgID.UI_RemoveAdsIcon;
    }
  }

  private rewardVisibleHorizontalBounds( type: OfferRewardDto['type'] ): { left: number; right: number }
  {
    switch ( type )
    {
      case 'coins': return { left: 11 / 210, right: 200 / 210 };
      case 'hearts': return { left: 17 / 192, right: 173 / 192 };
      case 'infinite_hearts_minutes': return { left: 13 / 121, right: 107 / 121 };
      case 'big_balls': return { left: 29 / 256, right: 229 / 256 };
      case 'infinite_balls': return { left: 50 / 256, right: 206 / 256 };
      case 'multi_balls': return { left: 14 / 256, right: 239 / 256 };
      case 'fireworks': return { left: 39 / 256, right: 235 / 256 };
      case 'mines': return { left: 20 / 257, right: 243 / 257 };
      default: return { left: 0, right: 1 };
    }
  }

  private rewardAmount( reward: OfferRewardDto ): string
  {
    if ( reward.type === 'no_ads_minutes' ) return formatMinutesShort( reward.amount );
    if ( reward.type === 'infinite_hearts_minutes' )
    {
      const hours = Math.floor( reward.amount / 60 );
      const minutes = reward.amount % 60;
      const h = LocalizationManager.t( "TIME.SHORT_HOURS" );
      const m = LocalizationManager.t( "TIME.SHORT_MINUTES" );
      return hours && minutes ? `${hours}${h} ${minutes}${m}` : hours ? `${hours}${h}` : `${minutes}${m}`;
    }
    return reward.type === 'coins' ? reward.amount.toLocaleString( 'uk-UA' ) : `x${reward.amount}`;
  }

  private createPurchaseButton(): void
  {
    const button_w = LayoutScaler.widthFromDesign( 618 );
    const button_h = LayoutScaler.heightFromDesign( 250 );
    const bonus_rows = Math.min( 2, Math.ceil( this.bonus_rewards.length / 4 ) );
    const button_y = this.adaptiveDialogY( [ 830, 1010, 1250 ][bonus_rows], 0.43 );
    const purchase_button = new UINineSliceElement(
      this.ctx,
      ( this.rect.getW() - button_w ) / 2,
      button_y,
      button_w,
      button_h,
      ImgID.UI_GreenFrameBtn,
      "frame_btn",
      undefined,
      undefined,
      true
    );

    const btn_text = new UITextElement(
      this.ctx,
      0,
      LayoutScaler.yFromDesign( 5 ),
      button_w,
      button_h,
      {
        text: "...",
        font_size: LayoutScaler.textSizeFromDesign( 66 ),
        font_weight: 900,
        text_style: TextStyleScheme.geologica,
        text_color: "#FFFFFF",
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_color: ColorScheme.more_lives_buy_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
        outline_offset_y: LayoutScaler.yFromDesign( 2 ),
        ...TextStyleScheme.buttonShadow(),
      }
    );

    purchase_button.addChild( btn_text, true );
    purchase_button.setOnClick( () => { void this.purchaseNoAds(); } );

    this.addChild( purchase_button );
    this.price_text = btn_text;
  }

  private setButtonText( text: string ): void
  {
    this.price_text?.setText( text );
    this.invalidateSubtree();
  }

  private updateTimer( now: number, force = false ): void
  {
    if ( !this.timer_text || this.timer_end_ms === null ) return;
    if ( !force && now < this.next_timer_update_at ) return;
    this.next_timer_update_at = now + 1000;

    const server_now = OfferService.getServerNowMs() ?? Date.now();
    const remaining_ms = Math.max( 0, this.timer_end_ms - server_now );
    this.timer_text.setText( formatShortDuration( remaining_ms ) );
    this.invalidateSubtree();

    if ( remaining_ms === 0 && !this.timer_expiry_refreshing )
      void this.refreshExpiredTimer();
  }

  private async refreshExpiredTimer(): Promise<void>
  {
    this.timer_expiry_refreshing = true;
    try
    {
      await OfferService.refresh();
      const current = OfferService.getPlacementOffer( this.placement_id );
      const next_deadline = current?.offer.id === this.offer.id
        ? OfferService.getOfferDisplayEndMs( current.offer )
        : null;
      const server_now = OfferService.getServerNowMs() ?? Date.now();

      if ( next_deadline !== null && next_deadline > server_now )
      {
        this.timer_end_ms = next_deadline;
        this.updateTimer( performance.now(), true );
      }
      else
      {
        this.setButtonText( "EXPIRED" );
      }
    }
    finally
    {
      this.timer_expiry_refreshing = false;
    }
  }

  private async refreshPrice(): Promise<void>
  {
    await OfferService.refresh();
    const current = OfferService.getPlacementOffer( this.placement_id );
    if ( !current || current.offer.id !== this.offer.id )
    {
      this.setButtonText( "N/A" );
      return;
    }
    const refreshed_deadline = OfferService.getOfferDisplayEndMs( current.offer );
    if ( this.timer_text && refreshed_deadline !== null )
    {
      this.timer_end_ms = refreshed_deadline;
      this.updateTimer( performance.now(), true );
      if ( refreshed_deadline <= ( OfferService.getServerNowMs() ?? Date.now() ) )
      {
        this.setButtonText( "EXPIRED" );
        return;
      }
    }
    await OfferService.trackShown( this.offer.id, { placement: "remove_ads_dialog", placement_id: this.placement_id, template: "remove_ads" } );

    if ( OfferService.hasNoAds() )
    {
      this.setButtonText( "OWNED" );
      return;
    }

    if ( OfferService.isFreeOffer( this.offer.id ) )
    {
      this.setButtonText( "FREE" );
      return;
    }

    const product_id = OfferService.getPlatformProductId( this.offer.id );
    if ( !product_id )
    {
      this.setButtonText( "N/A" );
      return;
    }

    try
    {
      const details = await IapPlugin.getProductDetails( product_id );
      this.setButtonText( details?.formatted_price ?? "BUY" );
      if ( !details?.formatted_price )
      {
        await OfferService.trackPriceUnavailable( this.offer.id, {
          placement: "remove_ads_dialog", placement_id: this.placement_id, template: "remove_ads", productid: product_id,
          failure_reason: "product_details_missing",
        } );
        return;
      }
      await OfferService.trackPriceLoaded( this.offer.id, {
        placement: "remove_ads_dialog", placement_id: this.placement_id, template: "remove_ads", productid: product_id,
        formatted_price: details?.formatted_price, currency: details?.currency, price_micros: details?.price_micros,
      } );
    }
    catch ( e )
    {
      console.warn( "[RemoveAds] price load failed", e );
      this.setButtonText( "BUY" );
      await OfferService.trackPriceUnavailable( this.offer.id, { placement: "remove_ads_dialog", placement_id: this.placement_id, template: "remove_ads", productid: product_id } );
    }
  }

  private async purchaseNoAds(): Promise<void>
  {
    if ( this.purchase_in_progress || OfferService.hasNoAds() ) return;
    if ( this.timer_end_ms !== null
      && this.timer_end_ms <= ( OfferService.getServerNowMs() ?? Date.now() ) ) return;

    this.purchase_in_progress = true;
    this.setButtonText( "..." );

    try
    {
      const context = { placement: "remove_ads_dialog", placement_id: this.placement_id, template: "remove_ads" };
      void OfferService.trackClicked( this.offer.id, context );
      const validation = OfferService.isFreeOffer( this.offer.id )
        ? await OfferService.claimFreeOffer( this.offer.id, context )
        : await OfferService.purchaseOffer( this.offer.id, context );
      if ( validation.player )
        adoptEconomyFromPlayer( validation.player );
      else
        await refreshPlayerFromServer();

      if ( ( 'entitlement' in validation && validation.entitlement?.active ) || OfferService.hasNoAds() )
      {
        this.setButtonText( "OWNED" );
        window.setTimeout( () => this.close(), 500 );
        return;
      }

      this.setButtonText( OfferService.isFreeOffer( this.offer.id ) ? "FREE" : "BUY" );
    }
    catch ( e )
    {
      console.warn( "[RemoveAds] purchase failed", e );
      this.setButtonText( OfferService.isFreeOffer( this.offer.id ) ? "FREE" : "BUY" );
      if ( !isPurchaseErrorReported( e ) )
        crashCapture( e, { phase: "remove_ads_purchase" } );
    }
    finally
    {
      this.purchase_in_progress = false;
    }
  }

  private createCloseButton(): void
  {
    const design_btn_size = 117;
    const btn_size = LayoutScaler.widthFromDesign( design_btn_size );
    const btn_x = this.rect.getW() - btn_size + LayoutScaler.widthFromDesign( 12 );
    // Keep the close button beside the timer even when the dialog body is
    // vertically re-centered on a tall screen.
    const btn_y = this.adaptiveDialogY( -125, 0.8 );

    const close_button = new DialogSafePressImage(
      this.ctx,
      btn_x,
      btn_y,
      btn_size,
      btn_size,
      ImgID.UI_SettingsDialogCloseBtn,
      {},
      true
    );
    close_button.setOnClick( () =>
    {
      void OfferService.trackClosed( this.offer.id, {
        placement: "remove_ads_dialog",
        placement_id: this.placement_id,
        template: "remove_ads"
      } );
      this.close();
    } );
    this.addChild( close_button );
  }

  public render(): void
  {
    this.startRender();
    this.finishRender();
  }

  public override update( now: number ): void
  {
    super.update( now );
    this.updateTimer( now );
  }

  public name(): string
  {
    return "RemoveAdsDialog";
  }
}
